import { useId, type CSSProperties, type ReactNode } from 'react';
import type { Sector } from '../variants/c/sectors';
import { Brand, Hero, Spark } from './bits';
import type { Composition, Head, Mod } from './compose';
import { phoneData } from './data';
import { Icon } from './icons';

// The owner page built from a maker's chosen modules (compose.ts). Classes are om-*, styled in ownerStyles.ts,
// and every skin restyles them through a handful of variables, so the same module looks native in each brand.

const d = (i: number) => ({ '--d': `${0.08 + i * 0.07}s` }) as CSSProperties;

function OwnerHead({ s, h, status }: { s: Sector; h: Head; status: string }) {
  return (
    <div className="op-head om-head">
      <Brand s={s} />
      <div className="om-head-row">
        <div className="om-head-txt">
          <div className="op-title">{h.title ?? s.fullModel}</div>
          <div className="op-status">
            <i />
            {status}
          </div>
        </div>
        {h.side === 'thumb' && (
          <span className="om-thumb">
            <img src={s.product} alt="" width={88} height={88} loading="lazy" />
          </span>
        )}
        {h.side === 'lock' && (
          <span className="om-lock">
            <span className="om-lock-k">
              <Icon name="lock" size={11} stroke={1.8} />
              Locked
            </span>
            <span className="om-switch">
              <i />
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function Ring({ pct, size = 30, w = 3.5 }: { pct: number; size?: number; w?: number }) {
  const r = (size - w) / 2;
  return (
    <svg className="om-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--om-track)" strokeWidth={w} />
      <circle className="om-ring-v" cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--om-hi)" strokeWidth={w} strokeLinecap="round" pathLength={1} strokeDasharray={`${pct} 1`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

function Flow({ m }: { m: Extract<Mod, { t: 'flow' }> }) {
  // Hub in the middle; solar feeds it, it feeds the home, the battery and the grid.
  const W = 228;
  const H = 136;
  const hub: [number, number] = [W / 2, 62];
  const nodes = {
    solar: [W / 2, 18],
    grid: [30, 62],
    home: [W - 30, 62],
    battery: [W / 2, 108],
  } as const;
  const path = (a: readonly [number, number], b: readonly [number, number]) => `M${a[0]} ${a[1]} L${b[0]} ${b[1]}`;
  return (
    <div className="op-card om-flow">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <path className="om-wire" d={path(nodes.solar, hub)} />
        <path className="om-wire" d={path(hub, nodes.home)} />
        <path className="om-wire" d={path(hub, nodes.grid)} />
        <path className="om-wire" d={path(hub, nodes.battery)} />
        <path className="om-pulse" d={path(nodes.solar, hub)} />
        <path className="om-pulse" d={path(hub, nodes.home)} />
        <path className="om-pulse" d={path(hub, nodes.grid)} />
        <path className="om-pulse" d={path(hub, nodes.battery)} />
        <circle cx={hub[0]} cy={hub[1]} r="3" fill="var(--om-hi)" />
      </svg>
      <span className="om-node" style={{ left: nodes.solar[0], top: nodes.solar[1] }}>
        <Icon name="sun" size={14} />
      </span>
      <span className="om-node-l is-r" style={{ left: nodes.solar[0] + 20, top: nodes.solar[1] - 9 }}>
        <span>Solar</span>
        <b>{m.solar}</b>
      </span>
      <span className="om-node" style={{ left: nodes.grid[0], top: nodes.grid[1] }}>
        <Icon name="pylon" size={14} />
      </span>
      <span className="om-node-l is-c" style={{ left: nodes.grid[0], top: nodes.grid[1] + 18 }}>
        <span>Grid</span>
        <b>{m.grid}</b>
      </span>
      <span className="om-node" style={{ left: nodes.home[0], top: nodes.home[1] }}>
        <Icon name="home" size={14} />
      </span>
      <span className="om-node-l is-c" style={{ left: nodes.home[0], top: nodes.home[1] + 18 }}>
        <span>Home</span>
        <b>{m.home}</b>
      </span>
      <span className="om-node is-bat" style={{ left: nodes.battery[0], top: nodes.battery[1] }}>
        <Ring pct={m.charge / 100} size={40} w={3} />
        <b>{m.charge}%</b>
      </span>
      <span className="om-node-l is-r" style={{ left: nodes.battery[0] + 26, top: nodes.battery[1] - 9 }}>
        <span>Battery</span>
        <b>Charging</b>
      </span>
    </div>
  );
}

function Dial({ m }: { m: Extract<Mod, { t: 'dial' }> }) {
  const S = 100;
  const r = 43;
  // 270 degree arc, 10°C to 40°C.
  const pct = Math.max(0, Math.min(1, (m.temp - 10) / 30));
  const gid = `om-dg${useId().replace(/:/g, '')}`;
  return (
    <div className="om-dial">
      <span className="om-dial-btn">−</span>
      <span className="om-dial-face">
        <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} aria-hidden="true">
          <circle cx={S / 2} cy={S / 2} r={r} fill="none" stroke="var(--om-track)" strokeWidth="6" strokeLinecap="round" pathLength={1} strokeDasharray=".75 1" transform={`rotate(135 ${S / 2} ${S / 2})`} />
          <circle className="om-ring-v" cx={S / 2} cy={S / 2} r={r} fill="none" stroke={`url(#${gid})`} strokeWidth="6" strokeLinecap="round" pathLength={1} strokeDasharray={`${0.75 * pct} 1`} transform={`rotate(135 ${S / 2} ${S / 2})`} />
          <defs>
            <linearGradient id={gid} x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" stopColor="var(--om-hi)" />
              <stop offset="1" stopColor="var(--om-warm, #F2A65A)" />
            </linearGradient>
          </defs>
        </svg>
        <span className="om-dial-t">
          <b>
            {m.temp}
            <sup>°C</sup>
          </b>
          <span>{m.target}</span>
        </span>
      </span>
      <span className="om-dial-btn">+</span>
      <span className="om-dial-sub">{m.sub}</span>
    </div>
  );
}

function Module({ s, m, i }: { s: Sector; m: Mod; i: number }): ReactNode {
  const pd = phoneData(s);
  switch (m.t) {
    case 'hero':
      return <Hero s={s} callouts={pd.callouts} pos={pd.heroPos} />;
    case 'photo':
      return (
        <div className="om-photo" style={d(i)}>
          <img src={s.product} alt="" width={484} height={336} loading="lazy" style={pd.heroPos ? { objectPosition: pd.heroPos } : undefined} />
          <span className="om-photo-tag">
            <Icon name="shield" size={10} stroke={1.8} />
            {m.tag}
          </span>
        </div>
      );
    case 'live':
      return pd.live ? (
        <div className="op-card op-live">
          <div className="op-live-txt">
            <span className="op-k">{pd.live.label}</span>
            <span className="op-big">
              {pd.live.value}
              <small>{pd.live.unit}</small>
            </span>
          </div>
          <Spark series={pd.live.series} />
          <span className="op-k2">{pd.live.sub}</span>
        </div>
      ) : null;
    case 'rows':
      return (
        <div className="op-card op-rows">
          {m.items.map((r, k) => (
            <div key={r.t} className={k === m.items.length - 1 ? 'op-row is-last' : 'op-row'}>
              <span className="op-ico">
                <Icon name={r.icon} size={13} />
              </span>
              <span className="op-row-txt">
                <b>{r.t}</b>
                <span>{r.sub}</span>
              </span>
              <Icon name="chev" size={11} className="op-chev" />
            </div>
          ))}
        </div>
      );
    case 'countdown':
      return (
        <div className="op-card om-count" style={d(i)}>
          <span className="om-k">
            <Icon name={m.icon} size={11} stroke={1.8} />
            {m.k}
          </span>
          <span className="om-count-big">
            <b>{m.big}</b>
            <small>{m.unit}</small>
          </span>
          <span className="om-bar">
            <i style={{ width: `${m.pct * 100}%` }} />
          </span>
          <span className="om-sub">{m.sub}</span>
        </div>
      );
    case 'checklist': {
      const done = m.items.filter((x) => x.done).length;
      return (
        <div className="op-card om-check" style={d(i)}>
          <div className="om-check-h">
            <b>{m.title}</b>
            <span>{m.meta}</span>
          </div>
          <span className="om-seg" aria-hidden="true">
            {m.items.map((x, k) => (
              <i key={k} className={x.done ? 'on' : undefined} />
            ))}
          </span>
          <ul>
            {m.items.map((x) => (
              <li key={x.t} className={x.done ? 'is-done' : undefined}>
                <span className="om-box">{x.done && <Icon name="check" size={9} stroke={2.4} />}</span>
                <span className="om-li-t">{x.t}</span>
                {x.note && <em>{x.note}</em>}
              </li>
            ))}
          </ul>
          {done === m.items.length && <span className="om-check-ok">All clear · signed</span>}
        </div>
      );
    }
    case 'holder':
      return (
        <div className="op-card om-holder" style={d(i)}>
          <span className="om-holder-i">
            <Icon name="pin" size={14} />
          </span>
          <span className="om-holder-t">
            <span className="om-k">{m.k}</span>
            <b>{m.who}</b>
            <span className="om-sub">{m.sub}</span>
          </span>
          <span className="om-badge">{m.badge}</span>
        </div>
      );
    case 'actions':
      return (
        <div className="om-actions" style={d(i)}>
          {m.items.map((a) => (
            <span key={a.t} className={a.alert ? 'om-act is-alert' : 'om-act'}>
              <Icon name={a.icon} size={15} stroke={1.8} />
              <b>{a.t}</b>
              <span>{a.sub}</span>
            </span>
          ))}
        </div>
      );
    case 'bars': {
      const max = Math.max(...m.days, 1);
      return (
        <div className="op-card om-bars" style={d(i)}>
          <div className="om-bars-t">
            <span className="om-k">{m.k}</span>
            <span className="om-bars-big">
              {m.value}
              <small>{m.unit}</small>
            </span>
            <span className="om-sub">{m.sub}</span>
          </div>
          <div className="om-bars-g" aria-hidden="true">
            {m.days.map((v, k) => (
              <span key={k} className="om-bar-c">
                <i style={{ height: `${Math.max(6, (v / max) * 100)}%`, '--d': `${0.2 + k * 0.05}s` } as CSSProperties} className={v ? undefined : 'is-zero'} />
                <em>{'MTWTFSS'[k]}</em>
              </span>
            ))}
          </div>
        </div>
      );
    }
    case 'tiles':
      return (
        <div className="om-tiles" style={{ ...d(i), gridTemplateColumns: `repeat(${m.items.length}, minmax(0, 1fr))` }}>
          {m.items.map((x) => (
            <span key={x.k} className="op-card om-tile">
              <span className="om-k">{x.k}</span>
              <span className="om-tile-v">
                {x.ring !== undefined && <Ring pct={x.ring} size={18} w={3} />}
                <b>{x.v}</b>
                {x.unit && <small>{x.unit}</small>}
              </span>
            </span>
          ))}
        </div>
      );
    case 'transfer':
      return (
        <div className="op-card om-xfer" style={d(i)}>
          <span className="om-xfer-i">
            <Icon name="transfer" size={14} />
          </span>
          <span className="om-holder-t">
            <b>{m.title}</b>
            <span className="om-sub">{m.sub}</span>
          </span>
          <span className="om-go">
            <Icon name="chev" size={11} stroke={2} />
          </span>
        </div>
      );
    case 'flow':
      return <Flow m={m} />;
    case 'contact':
      return (
        <div className="op-card om-contact" style={d(i)}>
          <span className="om-av">{m.who.slice(0, 1)}</span>
          <span className="om-holder-t">
            <span className="om-k">{m.k}</span>
            <b>{m.who}</b>
            <span className="om-sub">{m.sub}</span>
          </span>
          <span className="om-call">
            <Icon name="phone" size={12} stroke={1.7} />
          </span>
        </div>
      );
    case 'video':
      return (
        <div className="om-video" style={d(i)}>
          <span className="om-video-img">
            <img src={s.scene} alt="" width={240} height={120} loading="lazy" />
            <span className="om-play">
              <Icon name="play" size={12} stroke={1.6} />
            </span>
          </span>
          <span className="om-video-t">
            <b>{m.title}</b>
            <span>{m.sub}</span>
          </span>
        </div>
      );
    case 'reorder':
      return (
        <div className="op-card om-reorder" style={d(i)}>
          {m.title && <span className="om-k om-reorder-h">{m.title}</span>}
          {m.items.map((x, k) => (
            <div key={x.t} className="om-re-row">
              <span className="om-re-i">
                <Icon name="box" size={13} />
              </span>
              <span className="om-holder-t">
                <b>{x.t}</b>
                <span className={x.due ? 'om-sub is-due' : 'om-sub'}>{x.sub}</span>
              </span>
              {k === 0 ? <span className="om-btn">{m.cta}</span> : <span className="om-link">{m.cta}</span>}
            </div>
          ))}
        </div>
      );
    case 'dial':
      return <Dial m={m} />;
    case 'readings':
      return (
        <div className="op-card om-read" style={d(i)}>
          <div className="om-check-h">
            <b>
              <Icon name="drop" size={11} stroke={1.8} />
              {m.title}
            </b>
            <span>{m.meta}</span>
          </div>
          {m.items.map((x) => (
            <div key={x.k} className={x.warn ? 'om-read-r is-warn' : 'om-read-r'}>
              <span className="om-read-k">{x.k}</span>
              <span className="om-range">
                <i style={{ left: `${x.at * 100}%` }} />
              </span>
              <b>{x.v}</b>
            </div>
          ))}
        </div>
      );
    case 'book':
      return (
        <div className="op-card om-book" style={d(i)}>
          <div className="om-book-h">
            <Icon name="cal" size={13} />
            <span className="om-holder-t">
              <b>{m.title}</b>
              <span className="om-sub">{m.sub}</span>
            </span>
          </div>
          <div className="om-slots">
            {m.slots.map((x, k) => (
              <span key={x} className={k === 0 ? 'on' : undefined}>
                {x}
              </span>
            ))}
          </div>
        </div>
      );
    case 'ask':
      if (m.style === 'chip')
        return (
          <div className="om-askchip">
            <Icon name="ask" size={12} />
            <span>{m.text}</span>
            <Icon name="chev" size={10} stroke={2} />
          </div>
        );
      if (m.style === 'prompt')
        return (
          <div className="om-prompt">
            <span className="om-prompt-q">{m.text}</span>
            <span className="om-prompt-a">
              <span className="op-brand-mark is-sm">{s.mark}</span>
              Ask the {s.model}
              <Icon name="send" size={10} stroke={2} />
            </span>
          </div>
        );
      return (
        <div className="op-ask">
          <Icon name="ask" size={14} />
          <span>{m.text}</span>
          <span className="op-ask-go">
            <Icon name="send" size={11} stroke={2} />
          </span>
        </div>
      );
  }
}

export function ComposedHome({ s, c }: { s: Sector; c: Composition }) {
  const status = c.head.status ?? phoneData(s).status;
  return (
    <div className={`op-page om-page om-${s.key}`}>
      <OwnerHead s={s} h={c.head} status={status} />
      {c.mods.map((m, i) => (
        <Module key={`${m.t}${i}`} s={s} m={m} i={i} />
      ))}
    </div>
  );
}
